import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { DashboardContext } from '../contexts/DashboardContext';
import { UserContext } from '../contexts/UserContext';
import { getAnalyticsFilterOptions } from '../services/AnalyticsService';
import { analyticsRangeToDates, formatCountryLabel } from '../utils';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface AnalyticsFilterOptionsData {
  /** Campaign options - fetched from client-side dynamicPages */
  campaignId: FilterOption[];
  /** Device options - static */
  device: FilterOption[];
  /** OS options - from BigQuery */
  os: FilterOption[];
  /** Browser options - from BigQuery */
  browser: FilterOption[];
  /** Country options - from BigQuery */
  country: FilterOption[];
  /** City options - from BigQuery */
  city: FilterOption[];
  /** Ad network options - static */
  adNetworkProvider: FilterOption[];
  /** Referrer domain options - from BigQuery */
  referrerDomain: FilterOption[];
  /** Origin options - from BigQuery */
  origin: FilterOption[];
  /** UTM source options - from BigQuery */
  utmSource: FilterOption[];
  /** UTM medium options - from BigQuery */
  utmMedium: FilterOption[];
  /** Is bot options - static */
  isBot: FilterOption[];
  /** Bot name options - from BigQuery */
  botName: FilterOption[];
  /** Environment options - static */
  environment: FilterOption[];
}

const STATIC_DEVICE_OPTIONS: FilterOption[] = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'tablet', label: 'Tablet' },
];

const STATIC_AD_NETWORK_OPTIONS: FilterOption[] = [
  { value: 'google', label: 'Google' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'taboola', label: 'Taboola' },
  { value: 'outbrain', label: 'Outbrain' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'unknown', label: 'Unknown' },
];

const STATIC_IS_BOT_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const STATIC_ENVIRONMENT_OPTIONS: FilterOption[] = [
  { value: 'production', label: 'Production' },
  { value: 'test', label: 'Test' },
];

/**
 * Transforms BigQuery response to FilterOption array
 */
function transformToFilterOptions(
  data: { value: string; count: number }[] | undefined,
): FilterOption[] {
  if (!data) return [];
  return data.map((item) => ({
    value: item.value,
    label: item.value,
    count: item.count,
  }));
}

/**
 * Transforms BigQuery country response to FilterOption array with flags and names
 */
function transformCountryOptions(
  data: { value: string; count: number }[] | undefined,
): FilterOption[] {
  if (!data) return [];
  return data.map((item) => ({
    value: item.value,
    label: formatCountryLabel(item.value),
    count: item.count,
  }));
}

/**
 * Hook to fetch and provide filter options for the analytics filter builder.
 * Combines static options (device, ad network, isBot) with dynamic options
 * fetched from BigQuery (os, browser, country, etc.) and campaign options
 * from the client-side dynamicPages.
 */
export function useAnalyticsFilterOptions() {
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages, analyticsRange } = useContext(DashboardContext);

  // Calculate date range for the query
  const { startDate, endDate } = useMemo(() => {
    return analyticsRangeToDates(analyticsRange);
  }, [analyticsRange]);

  // Fetch dynamic filter options from BigQuery
  const {
    data: dynamicOptions,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'analytics-filter-options',
      selectedAccount?.id,
      startDate,
      endDate,
    ],
    queryFn: () =>
      getAnalyticsFilterOptions(selectedAccount!.id, startDate, endDate),
    enabled: !!selectedAccount?.id && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Build campaign options from dynamicPages
  const campaignOptions: FilterOption[] = useMemo(() => {
    if (!dynamicPages) return [];
    return dynamicPages.map((page) => ({
      value: page.id,
      label: page.data().name || page.id,
    }));
  }, [dynamicPages]);

  // Combine all options
  const options: AnalyticsFilterOptionsData = useMemo(
    () => ({
      campaignId: campaignOptions,
      device: STATIC_DEVICE_OPTIONS,
      os: transformToFilterOptions(dynamicOptions?.os),
      browser: transformToFilterOptions(dynamicOptions?.browser),
      country: transformCountryOptions(dynamicOptions?.country),
      city: transformToFilterOptions(dynamicOptions?.city),
      adNetworkProvider: STATIC_AD_NETWORK_OPTIONS,
      referrerDomain: transformToFilterOptions(dynamicOptions?.referrerDomain),
      origin: transformToFilterOptions(dynamicOptions?.origin),
      utmSource: transformToFilterOptions(dynamicOptions?.utmSource),
      utmMedium: transformToFilterOptions(dynamicOptions?.utmMedium),
      isBot: STATIC_IS_BOT_OPTIONS,
      botName: transformToFilterOptions(dynamicOptions?.botName),
      environment: STATIC_ENVIRONMENT_OPTIONS,
    }),
    [campaignOptions, dynamicOptions],
  );

  return {
    options,
    isLoading,
    error,
  };
}
